import { listTablePrefs } from 'app/constants'
import { RootState } from 'app/store'
import { ArrayElement } from 'core/actions/Action'
import DeleteDialog from 'core/components/DeleteDialog'
import ListContainer from 'core/containers/ListContainer'
import { GridViewColumn } from 'core/elements/grid/Grid'
import useListAction from 'core/hooks/useListAction'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import { routeManager } from 'core/utils/routes'
import DataKeys from 'k8s/DataKeys'
import { pick, prop } from 'ramda'
import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { formatTimestampToLocal } from '../../vms/helpers'
import { deleteSubnet, listNetworks } from '../actions'
import { subnetsSelector } from '../selectors'
import CreateSubnetModal from './CreateSubnetModal'
import EditSubnetModal from './EditSubnetModal'

type ModelDataKey = DataKeys.OpenstackSubnets
type SelectorModel = ArrayElement<ReturnType<typeof subnetsSelector>>

const defaultParams = {
  orderBy: 'created_at',
  orderDirection: 'desc',
}

const AllocationPoolsCell = ({ value }) => {
  return (
    <>
      {value.map((pool) => (
        <div>
          {pool.start} - {pool.end}
        </div>
      ))}
    </>
  )
}

const DnsNameServersCell = ({ value }) => {
  return (
    <>
      {value.map((dnsNameServer) => (
        <div>{dnsNameServer}</div>
      ))}
    </>
  )
}

const HostRoutesCell = ({ value }) => {
  return (
    <>
      {value.map((hostRoute) => (
        <div>
          {hostRoute.destination} to {hostRoute.nexthop}
        </div>
      ))}
    </>
  )
}

const columns: GridViewColumn<SelectorModel>[] = [
  {
    key: 'name',
    label: 'Name',
    width: 'medium',
  },
  {
    key: 'cidr',
    label: 'CIDR',
  },
  {
    key: 'ip_version',
    label: 'IP Version',
    render: (version) => `IPv${version}`,
  },
  {
    key: 'gateway_ip',
    label: 'Gateway IP',
  },
  {
    key: 'allocation_pools',
    label: 'Allocation Pools',
    CellComponent: AllocationPoolsCell,
  },
  {
    key: 'availableIps',
    label: 'Available IPs',
  },
  {
    key: 'enable_dhcp',
    label: 'DHCP',
    render: (enabled) => (enabled ? 'Enabled' : 'Disabled'),
  },
  {
    key: 'dns_publish_fixed_ip',
    label: 'DNS Publish Fixed IP',
    render: (enabled) => (enabled ? 'Enabled' : 'Disabled'),
  },
  {
    key: 'dns_nameservers',
    label: 'DNS Name Servers',
    CellComponent: DnsNameServersCell,
  },
  {
    key: 'host_routes',
    label: 'Host Routes',
    CellComponent: HostRoutesCell,
  },
  {
    key: 'created_at',
    label: 'Created',
    render: (time: string) => formatTimestampToLocal(time),
    display: false,
  },
]

const usePrefParams = createUsePrefParamsHook('Subnets', listTablePrefs)

export default function SubnetsPage({ network, loading, allowCrud = true }) {
  const session = useSelector<RootState, SessionState>(prop(sessionStoreKey))
  const { features } = session
  const { params, getParamsUpdater } = usePrefParams(defaultParams)
  const isVmware = features?.experimental?.pmov2_du_type === 'vmware'

  const { loading: loadingNetworks, reload: reloadNetworks } = useListAction(listNetworks, {
    params,
  })
  const subnets = network?.subnetDetails || []

  const EditSubnet = useCallback(
    (props: any) => {
      return <EditSubnetModal {...props} dnsDomain={network?.dns_domain} />
    },
    [network?.dns_domain],
  )

  const addRoute = isVmware
    ? routeManager.routes.openstack.createVmwSubnet
    : routeManager.routes.openstack.createNetworkSubnet

  const DeleteSubnetDialog = useCallback(
    (props) => (
      <DeleteDialog
        {...props}
        deleteAction={deleteSubnet}
        title="Delete Subnet(s)"
        resourceLabel="Subnets"
      />
    ),
    [],
  )

  return (
    <article>
      <CreateSubnetModal addRoute={addRoute} dnsDomain={network?.dns_domain} />
      <ListContainer<ModelDataKey, SelectorModel>
        showBreadcrumbs={false}
        dataKey={DataKeys.OpenstackSubnets}
        searchTargets={['name']}
        uniqueIdentifier="id"
        loading={loading || loadingNetworks}
        loadingMessage="Loading Subnets..."
        onRefresh={reloadNetworks}
        data={subnets}
        columns={columns}
        DeleteDialogComponent={DeleteSubnetDialog}
        addUrl={allowCrud ? addRoute.path({ id: network?.id }) : undefined}
        addText={allowCrud ? 'Create Subnet' : undefined}
        getParamsUpdater={getParamsUpdater}
        deleteAction={allowCrud ? deleteSubnet : undefined}
        EditDialogComponent={allowCrud ? EditSubnet : undefined}
        multiSelection
        {...pick(listTablePrefs, params)}
      />
    </article>
  )
}
